import { api } from "@/lib/api";

export interface ShiftBreak {
  id: string;
  started_at: string | null;
  ended_at: string | null;
  break_type: string | null;
}

export interface ShiftRow {
  id: string;
  direction: "IN" | "OUT" | string | null;
  status: string;
  note: string | null;
  starts_at: string | null;
  ends_at: string | null;
  is_open: boolean;
  breaks: ShiftBreak[];
}

export type ClockState = "IN" | "OUT" | "BREAK";

export interface ShiftsDashboardData {
  tz_name: string;
  tz_label: string;
  now: string;
  clock_state: ClockState;
  open_shift: { id: string; starts_at: string; ends_at: string | null } | null;
  open_break: { id: string; started_at: string } | null;
  today_schedule: { label: string | null; is_split: boolean };
  week: { hours: number; contract: number; pending: number };
  month: { hours: number; contract: number; pending: number };
  location: { policy: string; required_events: string[] };
  pagination: {
    page: number;
    page_size: number;
    total_pages: number;
    total_count: number;
  };
  on_sick_leave: boolean;
  shifts: ShiftRow[];
}

export interface ShiftActionResponse {
  ok: boolean;
  action: string;
  status: string;
  shift_id: string | null;
  starts_at: string | null;
  ends_at: string | null;
  break_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  location_required: boolean;
  location_saved: boolean;
  source: string;
}

export async function fetchShiftsDashboard(
  page: number,
  pageSize: number
): Promise<ShiftsDashboardData> {
  const { data } = await api.get<ShiftsDashboardData>("/api/web/shifts/my", {
    params: { page, page_size: pageSize },
  });
  return data;
}

interface ActionPayload {
  source: "web";
  // location omitted for now (Phase 4 first cut, no geolocation gating)
}

async function postShiftAction(path: string): Promise<ShiftActionResponse> {
  const payload: ActionPayload = { source: "web" };
  const { data } = await api.post<ShiftActionResponse>(path, payload, {
    headers: {
      "Idempotency-Key": cryptoRandomId(),
    },
  });
  return data;
}

export const clockIn = () => postShiftAction("/api/web/shifts/clock-in");
export const clockOut = () => postShiftAction("/api/web/shifts/clock-out");
export const breakStart = () => postShiftAction("/api/web/shifts/break-start");
export const breakEnd = () => postShiftAction("/api/web/shifts/break-end");

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
